/**
 * Vergroesserte Ansicht eines runden Bildes.
 *
 * Am Rechner geht sie beim Ueberfahren auf und beim Wegfahren wieder zu. Auf
 * dem Handy gibt es kein Ueberfahren: dort oeffnet ein Tippen auf das Bild,
 * und ein Tippen auf die Ansicht schliesst sie wieder.
 *
 * Die vergroesserte Fassung haengt am Dokument, nicht am Kaestchen. Der Grund
 * ist das Spaltenlayout der Inhaltsseiten: darin schneidet der Browser alles
 * weg, was ueber den Spaltenkasten hinausragt, und genau das tat die Lupe. Am
 * Dokument und fest am Bildschirm ausgerichtet kann sie niemand beschneiden.
 */
const RAND = 12;

/** Ohne Zeiger wird getippt statt ueberfahren. */
const OHNE_ZEIGER = window.matchMedia('(hover: none)');

/** Ob gerade die Tastatur fuehrt - dp-fokus.js setzt die Marke beim Tab. */
function perTastatur() {
  return document.documentElement.classList.contains('dp-tastatur');
}

/** @type {HTMLElement | null} */
let feld = null;

/** Wie lange eine Erklaerung nach dem Wegfahren noch steht - lange genug, um ihr X zu erreichen. */
const NACHLAUF = 250;
let nachlaufUhr = 0;

/**
 * Legt die Flaeche an, sobald sie zum ersten Mal gebraucht wird.
 *
 * @returns {HTMLElement}
 */
function hole() {
  if (feld) return feld;

  feld = document.createElement('span');
  feld.className = 'dp-lupe';
  feld.setAttribute('aria-hidden', 'true');
  feld.innerHTML =
    '<img alt=""><span class="dp-lupe__text"></span>' +
    '<button type="button" class="dp-lupe__zu" tabindex="-1" aria-label="Schließen">' +
    // Dasselbe X wie in jedem Fenster des Shops (Warenkorb, snippets/dp-kreuz.liquid) - 14er-Raster, Strich 1,6.
    '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false" fill="none">' +
    '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>';

  // Ein Tippen auf die Ansicht schliesst sie wieder.
  feld.addEventListener('click', verberge);

  /*
   * Das X oben rechts in einer Erklaerung schliesst sie. Der Tipp darauf darf
   * niemandem den Fokus nehmen: Steht die Erklaerung ueber einem Eingabefeld
   * mit offener Tastatur, bliebe sonst die Tastatur nicht offen. pointerdown
   * ist der Schritt, in dem der Browser den Fokus vergibt - dort abgesagt,
   * kommt der Klick trotzdem an.
   */
  const zu = feld.querySelector('.dp-lupe__zu');
  for (const art of ['pointerdown', 'mousedown']) {
    zu.addEventListener(art, (ereignis) => ereignis.preventDefault());
  }
  zu.addEventListener('click', (ereignis) => {
    ereignis.preventDefault();
    ereignis.stopPropagation();
    verberge();
  });

  /* Am Rechner: Wer mit dem Zeiger in die Erklaerung faehrt, um das X zu erreichen, haelt sie offen. */
  feld.addEventListener('pointerenter', (ereignis) => {
    if (ereignis.pointerType !== 'touch') window.clearTimeout(nachlaufUhr);
  });
  feld.addEventListener('pointerleave', (ereignis) => {
    if (ereignis.pointerType !== 'touch' && feld.classList.contains('dp-lupe--wort')) verbergeBald();
  });

  document.body.appendChild(feld);

  return feld;
}

/**
 * Zeigt die Aufnahme neben dem Kreis.
 *
 * @param {HTMLElement} anker
 */
function zeige(anker) {
  const quelle = anker.dataset.dpGross;
  if (!quelle) return;

  const flaeche = hole();
  const bild = flaeche.querySelector('img');
  if (bild.getAttribute('src') !== quelle) bild.src = quelle;
  bild.hidden = false;
  flaeche.querySelector('.dp-lupe__text').hidden = true;
  flaeche.classList.remove('dp-lupe--wort');

  flaeche.classList.add('dp-lupe--offen');
  flaeche.classList.toggle('dp-lupe--tippbar', OHNE_ZEIGER.matches);

  lege(flaeche, anker);
}

/**
 * Legt die Flaeche neben ihren Anker.
 *
 * Zuerst links davon, auf seiner Hoehe mittig. Passt sie dort nicht, weicht
 * sie nach rechts aus; senkrecht wird sie in den Bildschirm hineingeschoben,
 * falls sie oben oder unten anstiesse.
 *
 * @param {HTMLElement} flaeche
 * @param {HTMLElement} anker
 */
function lege(flaeche, anker, ueber) {
  /*
   * Steht der Anker in einem offenen modalen Fenster (Bewerten, Widerruf,
   * Fehlermeldung ...), zieht die Flaeche mit hinein: Ein modaler Dialog liegt
   * in der obersten Ebene, alles am Dokument darunter - die Erklaerung stand
   * sonst unsichtbar hinter dem Fenster (Jimmi, Oktober 2026). Danach wieder
   * zurueck ans Dokument.
   */
  const heim = anker.closest('dialog[open]') || document.body;
  if (flaeche.parentElement !== heim) heim.appendChild(flaeche);

  const rand = anker.getBoundingClientRect();
  const eigen = flaeche.getBoundingClientRect();

  let links;
  let oben;

  if (ueber) {
    /*
     * Eine Erklaerung steht mittig ueber ihrem Zeichen.
     *
     * Das Zeichen ist klein und sitzt am Ende einer Zeile - daneben ist oft
     * kein Platz, und die Flaeche rutschte dann an den Bildschirmrand, weit
     * weg von der Stelle, um die es geht. Ueber dem Zeichen steht sie immer
     * dort, wo man hinsieht. Passt sie nach oben nicht, geht sie darunter.
     */
    links = rand.left + rand.width / 2 - eigen.width / 2;
    links = Math.max(RAND, Math.min(links, window.innerWidth - eigen.width - RAND));

    /*
     * Gehoert das Zeichen zum Kauffenster, bleibt die Erklaerung in dessen
     * Kanten. Am Rechner steht das Fenster als schmale Spalte rechts; mittig
     * ueber einem Zeichen am rechten Rand ragte die Flaeche sonst darueber
     * hinaus und schwebte neben der Spalte in der Luft. Ist das Fenster
     * schmaler als die Flaeche, gilt weiter der Bildschirm - sonst waere sie
     * nicht mehr unterzubringen.
     */
    const fenster = anker.closest('.dp-kauffenster');
    if (fenster) {
      const kasten = fenster.getBoundingClientRect();
      if (kasten.width >= eigen.width) {
        links = Math.max(kasten.left, Math.min(links, kasten.right - eigen.width));
      }
    }

    oben = rand.top - eigen.height - RAND;
    if (oben < RAND) oben = rand.bottom + RAND;
    oben = Math.max(RAND, Math.min(oben, window.innerHeight - eigen.height - RAND));
  } else {
    links = rand.left - eigen.width - RAND;
    if (links < RAND) links = Math.min(rand.right + RAND, window.innerWidth - eigen.width - RAND);
    links = Math.max(RAND, links);

    oben = rand.top + rand.height / 2 - eigen.height / 2;
    oben = Math.max(RAND, Math.min(oben, window.innerHeight - eigen.height - RAND));
  }

  flaeche.style.insetInlineStart = Math.round(links) + 'px';
  flaeche.style.insetBlockStart = Math.round(oben) + 'px';
}

/**
 * Zeigt eine Erklaerung zu einem Begriff - in derselben Flaeche wie das Bild.
 *
 * @param {HTMLElement} anker
 * @param {string} text
 */
function zeigeWort(anker, text) {
  window.clearTimeout(nachlaufUhr);
  const flaeche = hole();
  const bild = flaeche.querySelector('img');
  const feldText = flaeche.querySelector('.dp-lupe__text');

  bild.hidden = true;
  feldText.hidden = false;
  feldText.textContent = text;
  /*
   * Hervorgehobene Teile (data-dp-info-teile, JSON [[text, fett], ...] - etwa
   * die Namen der Zielgruppen im Hero): Jeder Teil wird als Text eingesetzt,
   * fette in <strong> - nie als HTML, Sonderzeichen bleiben Zeichen. Der
   * reine Text (data-dp-info) ist derselbe ohne Auszeichnung.
   */
  if (anker.dataset.dpInfoTeile) {
    try {
      const teile = JSON.parse(anker.dataset.dpInfoTeile);
      if (Array.isArray(teile) && teile.length) {
        feldText.textContent = '';
        for (const teil of teile) {
          if (!Array.isArray(teil)) continue;
          const stueck = String(teil[0] ?? '');
          if (teil[1]) {
            const fett = document.createElement('strong');
            fett.textContent = stueck;
            feldText.appendChild(fett);
          } else {
            feldText.appendChild(document.createTextNode(stueck));
          }
        }
      }
    } catch {
      feldText.textContent = text;
    }
  }
  flaeche.classList.add('dp-lupe--wort', 'dp-lupe--offen');
  flaeche.classList.toggle('dp-lupe--tippbar', OHNE_ZEIGER.matches);

  /*
   * Passt die Erklaerung in eine Zeile, steht das X senkrecht mittig neben
   * ihr; bei mehreren Zeilen bleibt es oben rechts. Gemessen wird am Text
   * selbst - die Flaeche ist fest breit, also entscheidet allein seine Laenge.
   */
  const zeile = parseFloat(getComputedStyle(feldText).lineHeight) || 21;
  flaeche.classList.toggle('dp-lupe--einzeilig', feldText.getBoundingClientRect().height < zeile * 1.5);

  /* Die Erklaerung eines Zeichens steht ueber ihm, nicht daneben. */
  lege(flaeche, anker, true);
}

function verberge() {
  window.clearTimeout(nachlaufUhr);
  if (feld) feld.classList.remove('dp-lupe--offen');
}

/** Schliesst eine Erklaerung nach kurzem Nachlauf - die Zeit, mit dem Zeiger ihr X zu erreichen. */
function verbergeBald() {
  window.clearTimeout(nachlaufUhr);
  nachlaufUhr = window.setTimeout(verberge, NACHLAUF);
}

/**
 * @param {HTMLElement} anker
 */
function richteEin(anker) {
  if (anker.dataset.dpHorcht !== undefined) return;
  anker.dataset.dpHorcht = '';

  /*
   * Am Rechner fuehrt der Zeiger, auf dem Handy der Finger. Beides haengt am
   * selben Element; welcher Weg greift, entscheidet die Abfrage beim Ereignis
   * - so stimmt es auch, wenn ein Geraet beides kann.
   */
  anker.addEventListener('pointerenter', (ereignis) => {
    if (ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
    zeige(anker);
  });

  anker.addEventListener('pointerleave', (ereignis) => {
    if (ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
    verberge();
  });

  /*
   * Nur der Fokus von der Tastatur zeigt die Flaeche. Ein Tipp auf dem Handy
   * setzt den Fokus ebenfalls - erst kam so die Flaeche ueber den Fokus, und
   * der Klick gleich danach fand sie offen vor und schloss sie wieder: sie
   * ploppte kurz auf und verschwand. Ob die Tastatur fuehrt, weiss
   * dp-fokus.js (html.dp-tastatur).
   */
  anker.addEventListener('focus', () => {
    if (perTastatur()) zeige(anker);
  });
  anker.addEventListener('blur', verberge);

  anker.addEventListener('click', (ereignis) => {
    if (!OHNE_ZEIGER.matches) return;

    ereignis.preventDefault();
    if (feldOffen()) verberge();
    else zeige(anker);
  });
}

/**
 * Haengt an jeden Begriff, zu dem es eine Erklaerung gibt, ein kleines
 * Zeichen.
 *
 * Gesucht wird im Text der Kaesten, und zwar im Textknoten selbst - so bleibt
 * die Auszeichnung des Fliesstextes unberuehrt. Getroffen wird nur das erste
 * Vorkommen je Kasten: ein Begriff, der dreimal faellt, braucht nicht dreimal
 * dasselbe Zeichen.
 */
function haengeInfosAn() {
  const daten = document.querySelector('[data-dp-infos]');
  if (!daten) return;

  let infos = [];
  try {
    infos = JSON.parse(daten.textContent);
  } catch {
    return;
  }

  for (const info of infos) {
    if (!info.begriff || !info.text) continue;

    for (const feld of document.querySelectorAll('.dp-about__item-text')) {
      /*
       * Nur denselben Begriff nicht zweimal im selben Kasten - andere
       * Begriffe duerfen sehr wohl daneben stehen.
       */
      const schon = feld.dataset.dpBegriffe || '';
      if (schon.includes('|' + info.begriff + '|')) continue;

      const lauf = document.createTreeWalker(feld, NodeFilter.SHOW_TEXT);
      let knoten;
      let treffer = null;

      while ((knoten = lauf.nextNode())) {
        const stelle = knoten.textContent.indexOf(info.begriff);
        if (stelle >= 0) {
          treffer = { knoten, stelle };
          break;
        }
      }

      if (!treffer) continue;

      const rest = treffer.knoten.splitText(treffer.stelle + info.begriff.length);
      /*
       * Mit Verweis ist das Zeichen ein Link, ohne ein Knopf. So laesst sich
       * das Ziel anklicken, ohne dass der Zeiger in die aufgezogene Flaeche
       * wandern muss - die nimmt am Rechner keine Klicks an.
       */
      const zeichen = document.createElement(info.verweis ? 'a' : 'button');
      if (info.verweis) {
        zeichen.href = info.verweis;
        zeichen.target = '_blank';
        zeichen.rel = 'noopener noreferrer';
      } else {
        zeichen.type = 'button';
      }
      zeichen.className = 'dp-info';
      zeichen.dataset.dpInfo = info.text;
      // Bekommt seine Horcher gleich hier - die Sammelhorcher unten lassen es aus.
      zeichen.dataset.dpHorcht = '';
      zeichen.setAttribute('aria-label', 'Mehr zu ' + info.begriff);
      /* Dasselbe Zeichen wie im Menue des Headers. */
      zeichen.innerHTML =
        '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
        '<circle cx="8" cy="4.6" r="0.95" fill="currentColor"/>' +
        '<path d="M8 7.1v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>';
      /*
       * Das letzte Wort des Begriffs und das Zeichen bleiben zusammen in
       * einer Zeile (Oktober 2026): Am Zeilenende rutschte das Zeichen sonst
       * allein in die naechste Zeile, weg von dem Wort, zu dem es gehoert.
       */
      const vorn = treffer.knoten;
      const luecke = Math.max(vorn.textContent.lastIndexOf(' '), vorn.textContent.lastIndexOf('\u00a0'));
      const wort = luecke >= 0 ? vorn.splitText(luecke + 1) : vorn;
      const paar = document.createElement('span');
      paar.className = 'dp-info-paar';
      wort.parentNode.insertBefore(paar, wort);
      paar.append(wort, zeichen);
      feld.dataset.dpBegriffe = schon + '|' + info.begriff + '|';

      /*
       * Drei Wege zum selben Ziel: pointerenter, pointerover und mouseenter.
       * Nicht jede Umgebung schickt alle drei - eines davon kommt immer an,
       * und mehrfaches Aufziehen derselben Erklaerung stoert nicht.
       */
      const beiZeiger = (ereignis) => {
        if (ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
        zeigeWort(zeichen, info.text);
      };
      zeichen.addEventListener('pointerenter', beiZeiger);
      zeichen.addEventListener('pointerover', beiZeiger);
      zeichen.addEventListener('mouseenter', beiZeiger);
      zeichen.addEventListener('pointerleave', (ereignis) => {
        if (ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
        verbergeBald();
      });
      zeichen.addEventListener('focus', () => zeigeWort(zeichen, info.text));
      zeichen.addEventListener('blur', verberge);
      zeichen.addEventListener('click', (ereignis) => {
        /*
         * Auf dem Handy zeigt der erste Tipp die Erklaerung, der zweite folgt
         * dem Verweis. Am Rechner ist die Erklaerung beim Klicken laengst zu
         * sehen - dort geht es sofort los.
         */
        if (!OHNE_ZEIGER.matches) return;

        if (feldOffen()) return;

        ereignis.preventDefault();
        zeigeWort(zeichen, info.text);
      });
    }
  }
}

/**
 * @returns {boolean}
 */
function feldOffen() {
  return !!feld && feld.classList.contains('dp-lupe--offen');
}

/**
 * Das Info-Zeichen, dem ein Ereignis gilt - sofern es von woanders kommt.
 *
 * Andere Skripte legen eigene Zeichen an (dp-veredelung.js haengt
 * eines an jedes IMPRINT-Feld mit Hinweis) und geben ihnen nur die
 * Klasse dp-info und den Text in data-dp-info. Die Sammelhorcher unten
 * bedienen sie; die hier selbst gebauten Zeichen tragen dp-horcht und
 * haben ihre Horcher schon.
 *
 * @param {Event} ereignis
 * @returns {HTMLElement | null}
 */
function fremdesZeichen(ereignis) {
  const ziel = ereignis.target;
  if (!(ziel instanceof Element)) return null;
  const zeichen = ziel.closest('.dp-info[data-dp-info]');
  if (!(zeichen instanceof HTMLElement) || zeichen.dataset.dpHorcht !== undefined) return null;
  return zeichen;
}

/**
 * Sammelhorcher fuer fremde Info-Zeichen - am Dokument, damit auch Zeichen
 * bedient werden, die erst spaeter entstehen. Am Rechner zeigt das
 * Ueberfahren die Erklaerung, auf dem Handy der Tipp; ein zweiter Tipp
 * (oder einer auf die Erklaerung selbst) schliesst sie wieder.
 */
function horcheAufFremde() {
  document.addEventListener(
    'pointerover',
    (ereignis) => {
      const zeichen = fremdesZeichen(ereignis);
      if (!zeichen || ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
      zeigeWort(zeichen, zeichen.dataset.dpInfo || '');
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (ereignis) => {
      const zeichen = fremdesZeichen(ereignis);
      if (!zeichen || ereignis.pointerType === 'touch' || OHNE_ZEIGER.matches) return;
      // Innerhalb des Zeichens von Teil zu Teil - das ist kein Verlassen.
      if (ereignis.relatedTarget instanceof Node && zeichen.contains(ereignis.relatedTarget)) return;
      verbergeBald();
    },
    true
  );

  document.addEventListener(
    'focusin',
    (ereignis) => {
      const zeichen = fremdesZeichen(ereignis);
      // Nur von der Tastatur - der Tipp auf dem Handy setzt den Fokus auch (siehe richteEin).
      if (zeichen && perTastatur()) zeigeWort(zeichen, zeichen.dataset.dpInfo || '');
    },
    true
  );

  document.addEventListener(
    'focusout',
    (ereignis) => {
      if (fremdesZeichen(ereignis)) verberge();
    },
    true
  );

  document.addEventListener(
    'click',
    (ereignis) => {
      const zeichen = fremdesZeichen(ereignis);
      if (!zeichen) return;

      /*
       * Der Klick bleibt beim Zeichen: darunter liegt etwa das Eingabefeld,
       * dessen eigene Horcher sonst anspraengen.
       */
      ereignis.preventDefault();
      ereignis.stopPropagation();

      if (!OHNE_ZEIGER.matches) return;
      if (feldOffen()) {
        verberge();
        return;
      }
      zeigeWort(zeichen, zeichen.dataset.dpInfo || '');
    },
    true
  );
}

function starte() {
  for (const anker of document.querySelectorAll('[data-dp-gross]')) richteEin(anker);
  haengeInfosAn();
  horcheAufFremde();

  /*
   * Escape schliesst eine offene Erklaerung - vor allem das Fenster darunter bleibt dabei stehen
   * (Capture und stopImmediatePropagation: der Fensterbaustein, dp-fenster-fokus.js, sieht die Taste nicht).
   */
  document.addEventListener(
    'keydown',
    (ereignis) => {
      if (ereignis.key !== 'Escape' || !feldOffen()) return;
      ereignis.preventDefault();
      ereignis.stopImmediatePropagation();
      verberge();
    },
    true
  );

  // Beim Scrollen wandert der Kreis weg - die Lupe soll ihm nicht hinterherhaengen.
  window.addEventListener('scroll', verberge, { passive: true, capture: true });
  window.addEventListener('resize', verberge, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}
